/**
 * モックエッジリポジトリ実装
 * テストや開発用に利用するインメモリのリポジトリ
 */

import { v4 as uuidv4 } from 'uuid';
import { IEdgeRepository } from '@/repositories/interfaces';
import { TopicEdge } from '@/types/edgeTypes';
import { MockMapRepository } from './MockMapRepository';

/**
 * モックエッジリポジトリクラス
 */
export class MockEdgeRepository implements IEdgeRepository {
  /**
   * コンストラクタ
   * @param mapRepository マップリポジトリの参照
   */
  constructor(private mapRepository: MockMapRepository) {}

  /**
   * 指定IDのエッジを取得
   */
  public async getEdge(mapId: string, edgeId: string): Promise<TopicEdge | null> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return null;

    const edge = map.edges.find(e => e.id === edgeId);
    return edge ? { ...edge } : null;
  }

  /**
   * 指定マップの全エッジを取得
   */
  public async getEdges(mapId: string): Promise<TopicEdge[]> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return [];

    return map.edges.map(edge => ({ ...edge }));
  }

  /**
   * エッジを更新
   */
  public async updateEdge(
    mapId: string,
    edgeId: string,
    update: Partial<TopicEdge>
  ): Promise<TopicEdge | null> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return null;

    const edgeIndex = map.edges.findIndex(e => e.id === edgeId);
    if (edgeIndex === -1) return null;

    const updatedEdge = {
      ...map.edges[edgeIndex],
      ...update
    };

    const updatedEdges = [
      ...map.edges.slice(0, edgeIndex),
      updatedEdge,
      ...map.edges.slice(edgeIndex + 1)
    ];

    await this.mapRepository.updateMap(mapId, { edges: updatedEdges });
    return { ...updatedEdge };
  }

  /**
   * 新しいエッジを作成
   */
  public async createEdge(mapId: string, edge: Omit<TopicEdge, 'id'>): Promise<TopicEdge> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) throw new Error(`Map with ID ${mapId} not found`);

    const newEdge: TopicEdge = {
      ...edge,
      id: uuidv4()
    };

    const updatedEdges = [...map.edges, newEdge];
    await this.mapRepository.updateMap(mapId, { edges: updatedEdges });

    return { ...newEdge };
  }

  /**
   * エッジを削除
   */
  public async deleteEdge(mapId: string, edgeId: string): Promise<boolean> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return false;

    const edgeIndex = map.edges.findIndex(e => e.id === edgeId);
    if (edgeIndex === -1) return false;

    const updatedEdges = [
      ...map.edges.slice(0, edgeIndex),
      ...map.edges.slice(edgeIndex + 1)
    ];

    await this.mapRepository.updateMap(mapId, { edges: updatedEdges });
    return true;
  }

  /**
   * エッジのステータスを更新
   */
  public async updateEdgeStatus(
    mapId: string,
    edgeId: string,
    status: TopicEdge['status']
  ): Promise<boolean> {
    const edge = await this.getEdge(mapId, edgeId);
    if (!edge) return false;

    await this.updateEdge(mapId, edgeId, { status });
    return true;
  }

  /**
   * 複数のエッジを一括で更新
   */
  public async bulkUpdateEdges(
    mapId: string,
    edgesUpdates: { id: string; update: Partial<TopicEdge> }[]
  ): Promise<boolean> {
    const map = await this.mapRepository.getMap(mapId);
    if (!map) return false;

    const updatedEdges = [...map.edges];
    
    for (const { id, update } of edgesUpdates) {
      const edgeIndex = updatedEdges.findIndex(e => e.id === id);
      if (edgeIndex !== -1) {
        updatedEdges[edgeIndex] = {
          ...updatedEdges[edgeIndex],
          ...update
        };
      }
    }

    await this.mapRepository.updateMap(mapId, { edges: updatedEdges });
    return true;
  }
}